import getters from './getters'
import actions from './actions'
import mutations from './mutations'

export const REQUEST_VISIBILITY = {
  automatic: 'Automatic',
  staff_visible: 'Staff',
  embargoed: 'Embargoed',
  published: 'Published',
  department_visible: 'Department',
  department_published: 'Published - department only',
  restricted: 'Restricted'
} as const

export interface RequestVisibility {
  value: keyof typeof REQUEST_VISIBILITY
  name: (typeof REQUEST_VISIBILITY)[keyof typeof REQUEST_VISIBILITY]
}

export type RequestVisibilityValue = keyof typeof REQUEST_VISIBILITY

export type RequestVisibilityLabel =
  (typeof REQUEST_VISIBILITY)[keyof typeof REQUEST_VISIBILITY]

export interface AccountState {
  prizmdoc_proxy_url: string
  accept_terms: boolean
  all_requests_page_subheader: string
  anticipated_fulfillment_date: boolean
  background_image_file_name: string | null
  bucket_limit: number
  active_doc_size_in_bytes: number
  cybersource_activated: boolean
  departments: { id: number; name: string }[]
  document_review: boolean
  embargo_hours: number
  embargoing_available: boolean
  faq_header: string
  front_page_header: string
  front_page_text: string
  id: number
  legacy_request_show_enabled: boolean
  link_text: null
  link_url: null
  logo_file_name: null
  name: string
  payments: boolean
  payment_processor_activated: boolean
  pii_scan_feature_flag: boolean
  redaction: boolean
  request_page_subheader: string
  require_department: boolean
  required_fields: boolean
  restricted_visibilities_feature_flag: boolean
  retention: boolean
  show_cost_publicly: boolean
  show_dept_select: boolean
  show_due_date: boolean
  show_requester: boolean
  sidebar_request_text: string
  state: string
  stripe_activated: boolean
  subdomain: string
  subscribe: boolean
  terms_and_conditions: string
  uploaders_feature_flag: boolean
  visibility_default: RequestVisibilityValue
  notify_requesters_of_office_closures: boolean
  civicplus_pay_activated: boolean
  pinning_notes_feature_flag: boolean
}

export interface UserState {
  email: string
  email_or_name: string
  id: number
  name_with_email: string
  request_show_beta: boolean
  user_role: 'admin' | 'publisher' | 'guest' | 'staff' | 'department_admin'
}

export interface RuleState {
  actions: string[]
  subject: string[]
}

export interface JobMessage {
  status: string
  jobType: string
  jobId: string
  docIds: number[]
  prettyId: string
  payload: any
}

export interface State {
  account: AccountState | null
  currentUser: UserState | null
  previewUser: string | null
  previewUserId: string | null
  rules: RuleState[]
  lastJobMessage: JobMessage | null
}

export function accountRequestVisibilities(
  account: AccountState,
  includeAutomatic: boolean = false
): RequestVisibility[] {
  return Object.entries(REQUEST_VISIBILITY)
    .map(
      ([value, name]: [
        RequestVisibilityValue,
        RequestVisibilityLabel
      ]): RequestVisibility => ({
        value,
        name
      })
    )
    .filter(({ value }: { value: RequestVisibilityValue }) => {
      if (value === 'department_published') {
        return account.restricted_visibilities_feature_flag
      } else if (value === 'embargoed') {
        return account.embargoing_available
      } else if (!includeAutomatic && value === 'automatic') {
        return false
      }
      return true
    })
}

const globalModule: { state: State; [key: string]: any } = {
  state: {
    account: null,
    currentUser: null,
    previewUser: null,
    previewUserId: null,
    rules: [],
    lastJobMessage: null
  }, // data
  getters, // computed properties
  actions, // methods to retrieve data and call mutations
  mutations // setting and updating state
}

export { globalModule }
